import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { TxHashField } from './TxHashField';

interface DebtPaymentModalProps {
  transactionId: string;
  entityId: string;
  walletOutId?: string | null;
  walletInId?: string | null;
  /** Remaining OUT debt (we owe client) */
  debtAmountOut?: string | null;
  /** Remaining IN debt (client owes us) */
  debtAmountIn?: string | null;
  /** Original OUT debt */
  originalDebtAmountOut?: string | null;
  /** Original IN debt */
  originalDebtAmountIn?: string | null;
  /** Currency of the OUT leg */
  currencyOut?: string | null;
  /** Currency of the IN leg */
  currencyIn?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface LegFormState {
  amount: string;
  comment: string;
  error: string;
}

function PaymentLeg({
  direction,
  remaining,
  original,
  currency,
  walletId,
  walletAddress,
  walletType,
  transactionId,
  entityId,
  onSuccess,
}: {
  direction: 'in' | 'out';
  remaining: string;
  original: string | null | undefined;
  currency: string;
  walletId?: string | null;
  walletAddress?: string | null;
  walletType?: string;
  transactionId: string;
  entityId: string;
  onSuccess: (closed: boolean) => void;
}) {
  const { t } = useTranslation(['common']);
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const maxAmount = parseFloat(remaining) || 0;

  const [form, setForm] = useState<LegFormState>({
    amount: remaining !== '0' ? remaining : '',
    comment: '',
    error: '',
  });
  const [txHash, setTxHash] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.debt.addPayment(entityId, {
        transactionId,
        direction,
        amount: form.amount,
        currency,
        walletId: walletId ?? undefined,
        comment: form.comment || undefined,
        txHash: txHash.trim() || undefined,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['debt'] });
      queryClient.invalidateQueries({ queryKey: ['debt-detail'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction'] });
      queryClient.invalidateQueries({ queryKey: ['movements'] });

      if (result.transactionClosed) {
        success(t('cashier.debt.messages.fullyPaid', 'Transaction fully paid and closed'));
      } else {
        const rem = direction === 'out' ? result.remainingOut : result.remainingIn;
        success(t('cashier.debt.messages.partialPaid', 'Payment recorded. Remaining: {{remaining}}', { remaining: rem }));
      }
      onSuccess(result.transactionClosed);
    },
    onError: (err: any) => {
      setForm(f => ({ ...f, error: err.message || 'Payment failed' }));
      showError(err.message || 'Payment failed');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) {
      setForm(f => ({ ...f, error: 'Enter a positive amount' }));
      return;
    }
    if (amt > maxAmount) {
      setForm(f => ({ ...f, error: `Cannot exceed ${remaining} ${currency}` }));
      return;
    }
    setForm(f => ({ ...f, error: '' }));
    mutation.mutate();
  };

  const isOut = direction === 'out';
  const alreadyPaid = original
    ? Math.max(0, parseFloat(original) - parseFloat(remaining)).toFixed(2)
    : null;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4 rounded-xl border border-border bg-surface">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className={`text-lg ${isOut ? 'text-ctp-red' : 'text-ctp-teal'}`}>
          {isOut ? '📤' : '📥'}
        </span>
        <div>
          <div className="text-sm font-bold text-foreground">
            {isOut
              ? t('cashier.debt.outLeg', 'Выплатить клиенту')
              : t('cashier.debt.inLeg', 'Получить от клиента')}
          </div>
          <div className="text-[11px] text-foreground-muted">{isOut ? `wallet: ${walletId ? '✓' : '–'}` : `wallet: ${walletId ? '✓' : '–'}`}</div>
        </div>
      </div>

      {/* Summary row */}
      <div className="flex gap-6">
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">Outstanding</span>
          <span className={`text-xl font-bold font-mono ${isOut ? 'text-ctp-red' : 'text-ctp-teal'}`}>
            {remaining} {currency}
          </span>
        </div>
        {alreadyPaid && parseFloat(alreadyPaid) > 0 && (
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">Already Paid</span>
            <span className="text-xl font-bold font-mono text-ctp-green">{alreadyPaid} {currency}</span>
          </div>
        )}
      </div>

      {form.error && (
        <div className="bg-error/10 border border-ctp-red text-ctp-red px-3 py-2 rounded-md text-[13px]">
          {form.error}
        </div>
      )}

      {/* Amount input */}
      <div className="flex gap-3 items-end">
        <div className="flex-1 form-group">
          <label className="form-label">Amount ({currency})</label>
          <input
            type="text"
            className="form-input"
            placeholder={`Max: ${remaining}`}
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value.replace(/[^0-9.]/g, '') }))}
            autoFocus={isOut}
          />
        </div>
        <div className="flex-1 form-group">
          <label className="form-label">Comment</label>
          <input
            type="text"
            className="form-input"
            placeholder="Note..."
            maxLength={200}
            value={form.comment}
            onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
          />
        </div>
      </div>

      {walletType === 'crypto' && (
        <TxHashField
          walletAddress={walletAddress}
          walletType={walletType}
          value={txHash}
          onChange={setTxHash}
        />
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          className={`btn ${isOut ? 'btn-danger' : 'btn-primary'}`}
          disabled={mutation.isPending}
        >
          {mutation.isPending
            ? 'Recording...'
            : isOut
              ? t('cashier.debt.payOut', 'Записать выдачу')
              : t('cashier.debt.receiveIn', 'Записать получение')}
        </button>
      </div>
    </form>
  );
}

export function DebtPaymentModal({
  transactionId,
  entityId,
  walletOutId,
  walletInId,
  debtAmountOut,
  debtAmountIn,
  originalDebtAmountOut,
  originalDebtAmountIn,
  currencyOut,
  currencyIn,
  onClose,
  onSuccess,
}: DebtPaymentModalProps) {
  const { t } = useTranslation(['common']);

  const dOut = parseFloat(debtAmountOut || '0') || 0;
  const dIn  = parseFloat(debtAmountIn  || '0') || 0;

  // Fetch wallets to learn address + type for TxHashField network detection.
  const { data: walletList } = useQuery({
    queryKey: ['wallets', entityId],
    queryFn: () => api.admin.wallets.list(entityId),
    enabled: !!entityId,
  });
  const outWallet = walletList?.data.find(w => w.id === walletOutId);
  const inWallet = walletList?.data.find(w => w.id === walletInId);

  const handleLegSuccess = (closed: boolean) => {
    if (onSuccess) onSuccess();
    if (closed) onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={t('cashier.debt.recordPayment', 'Записать платёж')} size="lg">
      <div className="flex flex-col gap-4">
        {/* OUT leg: we owe client */}
        {dOut > 0 && currencyOut && (
          <PaymentLeg
            direction="out"
            remaining={debtAmountOut!}
            original={originalDebtAmountOut}
            currency={currencyOut}
            walletId={walletOutId}
            walletAddress={outWallet?.address}
            walletType={outWallet?.type}
            transactionId={transactionId}
            entityId={entityId}
            onSuccess={handleLegSuccess}
          />
        )}

        {/* IN leg: client owes us */}
        {dIn > 0 && currencyIn && (
          <PaymentLeg
            direction="in"
            remaining={debtAmountIn!}
            original={originalDebtAmountIn}
            currency={currencyIn}
            walletId={walletInId}
            walletAddress={inWallet?.address}
            walletType={inWallet?.type}
            transactionId={transactionId}
            entityId={entityId}
            onSuccess={handleLegSuccess}
          />
        )}

        {dOut <= 0 && dIn <= 0 && (
          <div className="text-center text-foreground-muted py-8">
            {t('cashier.debt.noOutstanding', 'No outstanding debt')}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-border">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
